import React from "react";
import Box from "@mui/material/Box";
import { apiUrls } from "../../actprep-ui/utils/urls";
import PaginatedTable from "../../actprep-ui/components/PaginatedTable";
import { dataGridNestedColumnGetter } from "../../actprep-ui/utils/tables";
import { weekDays } from "../../actprep-ui/utils/calendar";
import {
  sexyBooleanColumn,
  sexyBooleanColumnFalseOnly,
  sexyBooleanColumnTrueOnly,
} from "../../actprep-ui/constants/dataGrid";
import { tableNames } from "../../constants/tables";
import ASStudentEditForm from "./AccountabilitySheetEditForm";
import { fullName } from "../../actprep-ui/utils/formatting";
import { useFilters } from "../../actprep-ui/hooks/ui";
import { accountabilitySheetFilters } from "../../constants/filters";
import { Drawer } from "@mui/material";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import layout from "../../actprep-ui/constants/layout";
import { useTheme } from "@mui/material/styles";
import useResponsiveValue from "../../actprep-ui/hooks/ui/useResponsiveValue";

const successColor = "green";
const errorColor = "red";
const currentWeekValidators = {
  "current_week.check_ins": (val) => (val >= 7 ? "green" : "red"),
  "current_week.noon_check_ins": (val) => (val >= 5 ? "green" : "red"),
  "current_week.actual_hours": (val, rowData) => {
    let hoursNeeded = parseFloat(rowData.current_week.five_point_hours_needed);

    if (val >= 9 && val >= hoursNeeded) {
      return "green";
    } else if (val < 9 && val >= hoursNeeded) {
      return "yellow";
    }
    return "red";
  },
  "current_week.actual_score": (val, rowData) => {
    return val >= rowData.current_week.five_point_score_needed
      ? "green"
      : "red";
  },
  "current_week.quiz_score": (val) => {
    return val >= 80 ? "green" : "red";
  },
  "current_week.sent_count": (val) => {
    return val >= 5 ? "green" : "red";
  },
};

const previousWeekValidators = {
  "previous_week.check_ins": (val) => (val >= 7 ? "green" : "red"),
  "previous_week.noon_check_ins": (val) => (val >= 5 ? "green" : "red"),
  "previous_week.actual_hours": (val, rowData) => {
    let hoursNeeded = parseFloat(rowData.previous_week.five_point_hours_needed);

    if (val >= 9 && val >= hoursNeeded) {
      return "green";
    } else if (val < 9 && val >= hoursNeeded) {
      return "yellow";
    }
    return "red";
  },
  "previous_week.actual_score": (val, rowData) => {
    return val >= rowData.previous_week.five_point_score_needed
      ? "green"
      : "red";
  },
  "previous_week.quiz_score": (val) => {
    return val >= 80 ? "green" : "red";
  },
  "previous_week.sent_count": (val) => {
    return val >= 5 ? "green" : "red";
  },
};

const totalRedCalculator = (rowData, prefix) => {
  const validators =
    prefix == "current_week" ? currentWeekValidators : previousWeekValidators;
  const fieldsToValidate = Object.keys(validators);
  return fieldsToValidate
    .filter((field) => {
      const validator = validators[field];
      if (validator) {
        const val = rowData[prefix][field.split(".")[1]];
        const result = validator(val, rowData, field);
        return result == "red";
      }

      return !rowData[field];
    })
    .length.toString();
};

const bestNeededFormatter = ({ value }) => {
  const [best, needed] = value.split("/").map(Number);
  return (
    <Box
      sx={{
        backgroundColor: best >= needed ? successColor : errorColor,
        paddingLeft: ".5rem",
      }}
    >
      {value}
    </Box>
  );
};

const offDayFormatter = (val) => {
  if (val == null || val == undefined) {
    return;
  }
  return weekDays[val];
};

export default function AccountabilitySheet() {
  const filterDisc = useDisclosure();
  const { Filters, FiltersAppliedList, getFilterParams } = useFilters({
    name: "Accountability Sheet",
    saveState: true,
    filtersConfig: accountabilitySheetFilters,
    handleClose: filterDisc.onClose,
  });
  const columnWidthFactor = useResponsiveValue({
    xs: 0.5,
    md: 1,
  });
  const columns = [
    {
      field: "first_name",
      headerName: "First name",
      width: 100,
    },
    {
      field: "last_name",
      headerName: "Last name",
      width: 100,
    },
    {
      field: "current_team",
      headerName: "Team",
      valueGetter: dataGridNestedColumnGetter,
      width: 100,
      disableResponsiveWidth: true,
    },
    {
      field: "current_assistant_coach",
      headerName: "Assistant Coach",
      width: 150,
      disableResponsiveWidth: true,
    },
    {
      field: "school.name",
      headerName: "School",
      valueGetter: dataGridNestedColumnGetter,
      width: 150,
      disableResponsiveWidth: true,
    },
    {
      field: "cohort.event_name",
      headerName: "Cohort",
      valueGetter: dataGridNestedColumnGetter,
      valueFormatter: (val) => val?.replace(" Start Date", ""),
      width: 150,
      disableResponsiveWidth: true,
    },
    {
      field: "program_type",
      headerName: "Program Type",
      width: 100,
      disableResponsiveWidth: true,
    },
    {
      field: "seat_type",
      headerName: "Seat Type",
      width: 100,
      disableResponsiveWidth: true,
    },
    {
      field: "start_composite",
      headerName: "Starting Score",
      width: 100,
      disableResponsiveWidth: true,
    },
    {
      field: "goal_score",
      headerName: "Goal Score",
      width: 100,
      disableResponsiveWidth: true,
    },
    {
      field: "gpa",
      headerName: "GPA",
      width: 60,
      disableResponsiveWidth: true,
    },
    {
      field: "current_week.is_hours_volunteer",
      headerName: "Hours Volunteer?",
      type: "boolean",
      valueGetter: dataGridNestedColumnGetter,
      renderCell: sexyBooleanColumnTrueOnly,
    },
    {
      field: "current_week.in_the_game",
      headerName: "In The Game?",
      type: "boolean",
      valueGetter: dataGridNestedColumnGetter,
      renderCell: sexyBooleanColumnFalseOnly,
    },
    {
      field: "current_week.out_of_game_reason",
      headerName: "Out of Game Reason?",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "show_on_good_news",
      headerName: "Show on Good News?",
      type: "boolean",
      renderCell: sexyBooleanColumn,
    },
    {
      field: "is_leadership_team",
      headerName: "Leadership Team?",
      type: "boolean",
      renderCell: sexyBooleanColumnTrueOnly,
    },
    {
      field: "current_week.verbose_name",
      headerName: "Week",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.check_ins",
      headerName: "C",
      width: 50,
      disableResponsiveWidth: true,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.noon_check_ins",
      headerName: "Nc",
      width: 50,
      disableResponsiveWidth: true,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.sent_count",
      headerName: "A",
      width: 50,
      disableResponsiveWidth: true,
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.actual_hours",
      headerName: "H",
      width: 100,
      valueGetter: dataGridNestedColumnGetter,
      disableResponsiveWidth: true,
      valueFormatter: (val, row) => {
        val = parseFloat(val).toFixed(2);
        let hoursNeeded = parseFloat(
          row.current_week.five_point_hours_needed
        ).toFixed(2);
        hoursNeeded = hoursNeeded < 9 ? 9 : hoursNeeded;
        return `${val}/${hoursNeeded}`;
      },
    },
    {
      field: "current_week.quiz_score",
      headerName: "Q",
      width: 160,
      disableResponsiveWidth: true,
      valueGetter: dataGridNestedColumnGetter,
      renderCell: ({ value, row }) => {
        const previousScore =
          Math.round(row.previous_week?.quiz_score) || "N/A";
        const nextScore = Math.round(row.next_week_quiz_score) || "N/A";
        return (
          <span>
            <span style={{ fontWeight: "normal" }}>{previousScore}</span>
            {" - "}
            <span style={{ fontWeight: "bold", textDecoration: "underline" }}>
              {value}
            </span>
            {" - "}
            <span style={{ fontWeight: "normal" }}>{nextScore}</span>
            {": "}
            <span style={{ fontWeight: "normal" }}>
              {Math.round(row.quiz_average)}
            </span>
          </span>
        );
      },
    },
    {
      field: "current_week.actual_score",
      headerName: "S",
      width: 100,
      disableResponsiveWidth: true,
      valueGetter: dataGridNestedColumnGetter,
      valueFormatter: (val, row) =>
        `${val}/${row.current_week.five_point_score_needed} - ${row.tests_taken_this_week}`,
    },
    {
      field: "total_red",
      headerName: "R",
      width: 50,
      disableResponsiveWidth: true,
      valueGetter: (_, row) => totalRedCalculator(row, "current_week"),
    },
    {
      field: "previous_total_red",
      headerName: "LR",
      width: 50,
      disableResponsiveWidth: true,
      valueGetter: (_, row) =>
        row.previous_week ? totalRedCalculator(row, "previous_week") : null,
      valueFormatter: (val) => val ?? "N/A",
    },
    {
      field: "persistence_on_track",
      headerName: "P",
      width: 50,
      disableResponsiveWidth: true,
      type: "boolean",
      renderCell: sexyBooleanColumn,
    },
    {
      field: "foundation_on_track",
      headerName: "F",
      width: 50,
      disableResponsiveWidth: true,
      type: "boolean",
      renderCell: sexyBooleanColumn,
    },
    {
      field: "determination_on_track",
      headerName: "D",
      width: 50,
      disableResponsiveWidth: true,
      type: "boolean",
      renderCell: sexyBooleanColumn,
    },
    {
      field: "gold_award_on_track",
      headerName: "G",
      width: 50,
      disableResponsiveWidth: true,
      type: "boolean",
      renderCell: sexyBooleanColumn,
    },
    {
      field: "overall_effort_percentile",
      headerName: "R",
      width: 50,
      disableResponsiveWidth: true,
      valueFormatter: (val) => `${val}%`,
    },
    {
      field: "current_week.off_day",
      headerName: "Off Day",
      valueGetter: dataGridNestedColumnGetter,
      valueFormatter: offDayFormatter,
    },
    {
      field: "current_week.on_track_foundation_estimate",
      headerName: "Foundation Award Estimate",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.on_track_gold_estimate",
      headerName: "Gold Award Estimate",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.days_sent_picture",
      headerName: "Days Texted WS/CS",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.worksheet_questions",
      headerName: "Worksheet Questions Added",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.cheat_sheet_questions",
      headerName: "Cheat Sheet Questions Added",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.hours_needed",
      headerName: "Hours Needed",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.five_point_hours_needed",
      headerName: "Hours Needed (5PI)",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.score_needed",
      headerName: "Score Needed",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.five_point_score_needed",
      headerName: "Score Needed (5PI)",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "current_week.increase",
      headerName: "Increase",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "total_check_ins",
      headerName: "Total Check Ins",
    },
    {
      field: "total_noon_check_ins",
      headerName: "Total Noon Check Ins",
    },
    {
      field: "total_accuracy",
      headerName: "Total Accurate Days",
    },
    {
      field: "total_hours",
      headerName: "Total Hours",
    },
    {
      field: "prior_hours_needed",
      headerName: "Total Hours Needed (5PI)",
    },
    {
      field: "quiz_average",
      headerName: "Quiz Average",
      valueFormatter: (val) => `${Math.round(val)}%`,
    },
    {
      field: "total_increase",
      headerName: "Total Points Increased",
    },
    {
      field: "increase_per_hour",
      headerName: "Increase Per Hour",
    },
    {
      field: "start_english",
      headerName: "English",
    },
    {
      field: "start_math",
      headerName: "Math",
    },
    {
      field: "start_reading",
      headerName: "Reading",
    },
    {
      field: "start_science",
      headerName: "Science",
    },
    {
      field: "best_needed_english",
      headerName: "English",
      renderCell: bestNeededFormatter,
    },
    {
      field: "best_needed_math",
      headerName: "Math",
      renderCell: bestNeededFormatter,
    },
    {
      field: "best_needed_reading",
      headerName: "Reading",
      renderCell: bestNeededFormatter,
    },
    {
      field: "best_needed_science",
      headerName: "Science",
      renderCell: bestNeededFormatter,
    },
    {
      field: "projected_english",
      headerName: "English",
    },
    {
      field: "projected_math",
      headerName: "Math",
    },
    {
      field: "projected_reading",
      headerName: "Reading",
    },
    {
      field: "projected_science",
      headerName: "Science",
    },
    {
      field: "projected_composite",
      headerName: "Composite",
      valueGetter: (_, row) =>
        Math.round(
          ((row.projected_english || 0) +
            (row.projected_math || 0) +
            (row.projected_reading || 0) +
            (row.projected_science || 0)) /
            4
        ),
    },
    {
      field: "actions",
      type: "actions",
      width: 25,
      getActions: () => [],
    },
  ];
  const accountabilityFields = [
    "current_week.check_ins",
    "current_week.noon_check_ins",
    "current_week.sent_count",
    "current_week.actual_hours",
    "current_week.quiz_score",
    "current_week.actual_score",
  ];
  const theme = useTheme();
  return (
    <>
      <Drawer anchor="right" {...filterDisc.getDisclosureProps()}>
        <Box
          sx={{ paddingTop: `${layout.navBarHeight + 1}rem`, width: "25rem" }}
        >
          <Filters />
        </Box>
      </Drawer>
      <PaginatedTable
        getCellClassName={(params) => {
          const field = params.colDef.field;
          if (!accountabilityFields.includes(field)) {
            return "";
          }
          const flagResult = currentWeekValidators[field](
            params.value,
            params.row
          );
          return `${flagResult}-flag accountability-field`;
        }}
        columnWidthFactor={columnWidthFactor}
        tableName={tableNames.ACCOUNTABILITY_SHEET}
        columns={columns}
        searchable
        apiUrl={apiUrls.admin.accountability()}
        disableCellFocus
        urlParams={{
          search_params: getFilterParams(),
          retry: false,
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 ? "even" : "odd"
        }
        sx={{
          "& .MuiDataGrid-cell.accountability-field": {
            pl: 0.5,
            fontWeight: "bold",
            color: "black",
            border: `3px solid ${theme.palette.background.secondary}`,
            textAlign: "center",
          },
          "& .MuiDataGrid-row.even": {
            backgroundColor: "rgba(0, 0, 0, .05)",
          },
          "& .MuiDataGrid-cell.accountability-field.green-flag": {
            backgroundColor: "green",
          },
          "& .MuiDataGrid-cell.accountability-field.red-flag": {
            backgroundColor: "red",
          },
          "& .MuiDataGrid-cell.accountability-field.yellow-flag": {
            backgroundColor: "yellow",
          },
          "& .MuiDataGrid-cell": {
            fontSize: "calc(7px + 0.5vw)",
          },
          "& .MuiDataGrid-cell--pinnedLeft,": {
            backgroundColor: theme.palette.background.paper,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            fontSize: "calc(7px + 0.5vw)",
          },
        }}
        initialState={{
          pinnedColumns: {
            right: ["actions"],
            left: ["first_name", "last_name"],
          },
        }}
        onFiltersClick={filterDisc.onToggle}
        FiltersAppliedList={FiltersAppliedList}
        getRowTitle={({ row }) => fullName(row)}
        EditForm={ASStudentEditForm}
        disableColumnMenu
        columnGroupingModel={[
          {
            groupId: "Name",
            children: [{ field: "first_name" }, { field: "last_name" }],
          },
          {
            groupId: "Primary Data",
            children: [
              { field: "current_team" },
              { field: "current_assistant_coach" },
              { field: "school.name" },
              { field: "cohort.event_name" },
              { field: "program_type" },
              { field: "seat_type" },
              { field: "start_composite" },
              { field: "goal_score" },
              { field: "gpa" },
              { field: "current_week.is_hours_volunteer" },
              { field: "current_week.in_the_game" },
              { field: "current_week.out_of_game_reason" },
              { field: "show_on_good_news" },
              { field: "is_leadership_team" },
            ],
          },
          {
            groupId: "Week Stats",
            children: [
              { field: "current_week.verbose_name" },
              { field: "current_week.check_ins" },
              { field: "current_week.noon_check_ins" },
              { field: "current_week.sent_count" },
              { field: "current_week.actual_hours" },
              { field: "current_week.quiz_score" },
              { field: "current_week.actual_score" },
              { field: "total_red" },
              { field: "previous_total_red" },
              { field: "persistence_on_track" },
              { field: "foundation_on_track" },
              { field: "determination_on_track" },
              { field: "gold_award_on_track" },
              { field: "overall_effort_percentile" },
              { field: "current_week.off_day" },
              { field: "current_week.on_track_foundation_estimate" },
              { field: "current_week.on_track_gold_estimate" },
              { field: "current_week.days_sent_picture" },
              { field: "current_week.worksheet_questions" },
              { field: "current_week.cheat_sheet_questions" },
              { field: "current_week.hours_needed" },
              { field: "current_week.five_point_hours_needed" },
              { field: "current_week.score_needed" },
              { field: "current_week.five_point_score_needed" },
              { field: "current_week.increase" },
            ],
          },
          {
            groupId: "Overall Stats",
            children: [
              { field: "total_check_ins" },
              { field: "total_noon_check_ins" },
              { field: "total_accuracy" },
              { field: "total_hours" },
              { field: "prior_hours_needed" },
              { field: "quiz_average" },
              { field: "total_increase" },
              { field: "increase_per_hour" },
            ],
          },
          {
            groupId: "Starting Scores",
            children: [
              { field: "start_english" },
              { field: "start_math" },
              { field: "start_reading" },
              { field: "start_science" },
            ],
          },
          {
            groupId: "Best/Needed Scores",
            children: [
              { field: "best_needed_english" },
              { field: "best_needed_math" },
              { field: "best_needed_reading" },
              { field: "best_needed_science" },
            ],
          },
          {
            groupId: "Projected Scores",
            children: [
              { field: "projected_english" },
              { field: "projected_math" },
              { field: "projected_reading" },
              { field: "projected_science" },
              { field: "projected_composite" },
            ],
          },
        ]}
      />
    </>
  );
}
